/**
 * Local copy of `useDebouncerPlus`, the quantity-input debounce that waits until the customer
 * has also stopped using the neighbouring UOM dropdowns before re-pricing.
 *
 * Only {@link DebouncerPlusEvents} is imported from this file: the catalog, product-details,
 * quote-details and shopping-list price/UOM cells publish `to-debounce` messages with it. The
 * hooks that receive those messages are the copy in `@ryerson/frontend.common` and
 * `Shared/QuickEntry/Helpers/V2/QuantityUOMV2.tsx` → `setSelectInteractionResult`; this file's
 * hook has no caller. The receivers compare the event name against their own lists of DOM event
 * names, so the enum values must stay plain DOM event names.
 *
 * The live message channel is a `@ryerson/frontend.common` → `Subscribable`, created by each
 * feature's context (for example `Catalog/Context/CatalogContext.tsx`) and passed down as
 * `stateMessenger`. This file types its parameter with the local copy in
 * `Shared/StateManager/StateManager.tsx`, which nothing else imports.
 *
 * Message protocol:
 * - in: `("to-debounce", eventName, id)`: another control on the row was focused or changed.
 * - out: the package hook publishes `("is-debouncing", true | false, id)` when a debounce starts
 *   or ends, and the price / UOM cells read it. This file's hook publishes the same message with
 *   a fourth argument, `"quantity-input"`.
 */
import React from "react";
import { Subscribable } from "@components/Shared/StateManager/StateManager";

/**
 * DOM event names a UOM dropdown sends in a `to-debounce` message. In `QuantityUOMV2.tsx` and in
 * this file's hook, `focus` marks the customer as interacting and `change` ends it. The
 * `@ryerson/frontend.common` hook treats only `change` (not `focus`) as meaningful.
 */

export enum DebouncerPlusEvents {
	focus = "focus",
	change = "change",
}

/** Whether the input `id` is currently waiting out a debounce; published as `is-debouncing`. */
export interface DebounceStatus {
	id: string | number;
	value: boolean;
}

/**
 * Debounces `value` and also waits for the customer to finish with related controls.
 *
 * @param id - the row or input id carried in published messages.
 * @param delay - timeout length, and also the idle threshold: the value is released only when
 *   no watched event has fired for longer than `delay` ms.
 * @param idsToWatch - DOM ids of other inputs (UOM dropdowns) whose mousedown, mouseout, focus,
 *   mouseover and change events count as activity. Activity resets the timer, and between a
 *   mousedown or focus on a `<select>` and its change or mouseout the value is held. A mouseout
 *   directly after a focus or another mouseout does not end the hold. `blur` ends it only when
 *   it arrives as a `to-debounce` message, since no blur listener is bound.
 * @param stateMessenger - pub/sub channel for the `to-debounce` / `is-debouncing` messages
 *   described in the file header.
 * @param isOkToDebounce - when false, a new `value` does not start the timer.
 * @returns `undefined` until the timeout has passed with no activity, then the value. It resets
 *   to `undefined` whenever `value` changes.
 */
const useDebouncerPlus = (
	id: number | string,
	value: any,
	delay: number,
	idsToWatch: Array<string> = [],
	stateMessenger?: Subscribable<string>,
	isOkToDebounce: boolean = true
): any => {
	const $query = document.querySelector.bind(document);
	const [isMounted, setIsMounted] = React.useState<boolean>();
	const [debouncedValue, setDebouncedValue] = React.useState<any>();
	// create state array to hold the messages from PUB SUB
	const [message, setMessage] = React.useState<Array<any>>([]);

	let lastEventTime = React.useRef<any>();
	let timeOutId = React.useRef<number>();
	let isUserInteracting = React.useRef<boolean>(false);
	let accumulatedEvents = React.useRef<Array<string>>([]);
	let eventsToBind = ["mousedown", "mouseout", "focus", "mouseover", "change"];

	const [debouncingStatus, setDebouncingStatus] = React.useState<DebounceStatus>();

	//let curTimeout: any;

	/**
	 * @dec - Binds a list of events to a list of events, routing callback to to eventTracker()
	 * @param elementSelector - lis of strings signifying classes,id's, or tagname
	 * @param byType -to  query DOM selectors by class , id, or tagname(i.e "div" , "body" etc)
	 * @param eventsList - list of events ("mousedown","mouseup" etc..) to bind for each element in elementSelector
	 */
	const bindListeners = function (
		elementSelector: Array<string> = idsToWatch,
		byType: "id" | "class" | "tagname" = "id",
		eventsList: Array<string> = eventsToBind,
		callback: any = eventTracker,
		eventBubbling: boolean = true
	) {
		unBindListeners();
		const identifier = byType === "id" ? "#" : byType === "class" ? "." : "";
		if (elementSelector.length && eventsList.length) {
			elementSelector.forEach((selector: string, indexEl: number) => {
				let El = $query(`${identifier}${selector}`);

				eventsList.forEach((theEventName: string, indexEvent: number) => {
					El && attachEvent(El, theEventName, callback, eventBubbling);
				});
			});
		}
	};

	/**
	 * @dec - UnBinds a list of events to a list of events, routing callback to to eventTracker()
	 * @param elementSelector - lis of strings signifying classes,id's, or tagname
	 * @param byType -to  query DOM selectors by class , id, or tagname(i.e "div" , "body" etc)
	 * @param eventsList - list of events ("mousedown","mouseup" etc..) to bind for each element in elementSelector
	 */
	const unBindListeners = function (
		elementSelector: Array<string> = idsToWatch,
		byType: "id" | "class" | "tagname" = "id",
		eventsList: Array<string> = eventsToBind,
		callback: any = eventTracker
	) {
		if (elementSelector.length && eventsList.length) {
			const identifier = byType === "id" ? "#" : byType === "class" ? "." : "";

			elementSelector.forEach((selector: string) => {
				let El = $query(`${identifier}${selector}`);
				eventsToBind.forEach((theEvenName: string) => {
					El && El?.removeEventListener(theEvenName, callback, true);
					El?.removeAttribute(`has-${theEvenName}`);
				});
			});
		}
	};

	const attachEvent = (
		element: Element,
		eventName: string,
		callback: any = eventTracker,
		eventBubbling: boolean = true
	) => {
		if (element && eventName && !element.hasAttribute(`has-${eventName}`)) {
			element.setAttribute(`has-${eventName}`, "true");
			element.addEventListener(eventName, callback, eventBubbling);
		}
	};

	const checkElapseTimeSinceLastEvent = () => {
		let cur = Math.floor(Date.now());
		let last = Math.floor(lastEventTime.current.getTime());

		return cur - last;
	};

	const setSelectInteractionResult = (event: string) => {
		let previousEventType = accumulatedEvents.current[0] || "";

		accumulatedEvents.current.push(event);

		const selectLeaveEvents = ["blur", "change", "mouseout"];
		const selectEnterEvents = ["mousedown", "click", "focus"];

		if (selectEnterEvents.includes(event)) {
			isUserInteracting.current = true;
		}

		if (selectLeaveEvents.includes(event)) {
			// this is to mitigate the issue of firefox firing mouseout event and focus at same time!
			let firefoxEventIssue = !!(
				event === "mouseout" &&
				(previousEventType === "focus" || previousEventType === "mouseout")
			);
			if (!firefoxEventIssue) {
				isUserInteracting.current = false;
				setDebouncedValue(undefined);
			}
		}

		accumulatedEvents.current.length >= 2 && accumulatedEvents.current.shift();
	};

	const eventTracker = function (e: Event) {
		// Record last event time
		lastEventTime.current = new Date();

		// determine is select dropdown are being used
		let tagName = (e.target as Element).tagName;
		if (tagName === "SELECT") {
			setSelectInteractionResult(e.type);
		}
	};

	/** @desc -Listener for Pub Sub messages FROM other components	*/
	React.useEffect(() => {
		let [messageString, value] = message;
		// determine is (external) select dropdown are being used
		messageString === "to-debounce" && setSelectInteractionResult(value);
	}, [message]);

	/** Publishes `is-debouncing` TO other components whenever the debouncing state flips. */
	React.useEffect(() => {
		if (debouncingStatus) {
			publishMessage(
				`is-debouncing`,
				debouncingStatus.value,
				debouncingStatus.id,
				"quantity-input"
			);
		}
	}, [debouncingStatus?.value]);

	const setTimer = () => {
		//publishMessage(`is-debouncing`, true, id);
		setDebouncingStatus({ id, value: true });

		if (timeOutId.current) {
			clearTimeout(timeOutId.current);
			timeOutId.current = undefined;
		}

		bindListeners();

		timeOutId.current =
			window &&
			window?.setTimeout(() => {
				let haveBeenIdle = checkElapseTimeSinceLastEvent() > delay;
				let interActionComplete = !isUserInteracting.current;

				if (haveBeenIdle && interActionComplete) {
					unBindListeners();
					setDebouncedValue(value);
					clearTimeout(timeOutId.current);
					timeOutId.current = undefined;

					setDebouncingStatus({ id, value: false });
					//publishMessage(`is-debouncing`, false, id);
				} else {
					setTimer();
				}
			}, delay);
	};

	// Use Typed a value to debounce
	React.useEffect(() => {
		setDebouncedValue(undefined);
		isUserInteracting.current = false;
		isMounted && value && isOkToDebounce && setTimer();
		isMounted && value && (lastEventTime.current = new Date());
	}, [value, delay]);

	const publishMessage = (MessageString: string, data: any, topic: any, info: any) => {
		setTimeout(() => stateMessenger?.publish(MessageString, data, topic, info), 10);
	};

	React.useEffect(() => {
		// Subscribe to pub-sub  stateMessenger if available
		stateMessenger?.subscribe((msg: string, value: any, topic: any, info: any) =>
			setMessage([msg, value, topic, info])
		);
		setIsMounted(true);
	}, []);

	debouncedValue && clearTimeout(timeOutId.current);

	return debouncedValue;
};

export default useDebouncerPlus;
